import { Component, DestroyRef, OnInit, inject, viewChild } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { ThemeToggle } from '../../components/theme-toggle/theme-toggle';
import { AuthService } from '../../auth/services/auth.service';
import { OrganizationStore } from '../services/organization.store';

interface NavItem {
  label: string;
  icon: string;
  route: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

@Component({
  selector: 'app-navigation',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    ThemeToggle,
  ],
  templateUrl: './navigation.html',
  styleUrl: './navigation.css',
})
export class Navigation implements OnInit {
  private readonly sidenav = viewChild<MatSidenav>('sidenav');

  private readonly auth = inject(AuthService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  readonly orgStore = inject(OrganizationStore);

  isHandset = false;

  readonly overviewItems: NavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
  ];

  readonly cadastroItems: NavItem[] = [
    { label: 'Clientes', icon: 'people', route: '/clientes' },
    { label: 'Fornecedores', icon: 'local_shipping', route: '/fornecedores' },
    { label: 'Produtos', icon: 'inventory_2', route: '/produtos' },
    { label: 'Categorias', icon: 'category', route: '/categorias' },
  ];

  readonly operacaoItems: NavItem[] = [
    { label: 'Vendas', icon: 'receipt_long', route: '/vendas' },
    { label: 'Caixa', icon: 'point_of_sale', route: '/caixa' },
    { label: 'Despesas', icon: 'request_quote', route: '/despesas' },
    { label: 'Entradas de Estoque', icon: 'add_box', route: '/estoque/entradas' },
    { label: 'Movimentações', icon: 'swap_horiz', route: '/estoque/movimentacoes' },
  ];

  readonly relatorioItems: NavItem[] = [
    { label: 'Vendas', icon: 'summarize', route: '/relatorios/vendas' },
    { label: 'Despesas', icon: 'receipt_long', route: '/relatorios/despesas' },
    { label: 'Vendas vs Despesas', icon: 'balance', route: '/relatorios/vendas-vs-despesas' },
    { label: 'Fechamento', icon: 'point_of_sale', route: '/relatorios/fechamento' },
  ];

  readonly configItems: NavItem[] = [
    { label: 'Configurações', icon: 'settings', route: '/configuracoes' },
  ];

  /** Drives the sidenav: one @for instead of five near-identical blocks. */
  readonly navSections: NavSection[] = [
    { label: 'Visão Geral', items: this.overviewItems },
    { label: 'Cadastros', items: this.cadastroItems },
    { label: 'Operações', items: this.operacaoItems },
    { label: 'Relatórios', items: this.relatorioItems },
    { label: 'Configurações', items: this.configItems },
  ];

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isHandset = result.matches;
        const sidenav = this.sidenav();
        if (!sidenav) return;

        if (result.matches) {
          sidenav.close();
        } else {
          sidenav.open();
        }
      });

    this.orgStore.loadConfig();
  }

  logout(): void {
    this.auth.logout();
  }
}
